// src/Api.js
import axios from 'axios';

const API_URL = 'https://konnectfbr.com/api';
//const API_URL = 'http://localhost:3060';


// Read token dynamically from localStorage (or other secure storage)
const getAuthToken = () => {
  return localStorage.getItem('token') || null;
};

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach token before every request
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers['token'] = token; // backend expects `token` header
  } else {
    delete config.headers['token']; // remove if missing
  }
  return config;
});

/** ===================== Auth ===================== **/
export const login = async (email, password) => {
  const res = await api.post('/auth_admin', { email, password });

  // Save token from backend response for later requests
  const token = res?.data?.response?.token;
  if (token) {
    localStorage.setItem('token', token);
  }

  return res;
};

/** ===================== Dashboard ===================== **/
export const getDashboard = () => {
  return api.get('/dasboard_admin');
};

/** ===================== Users ===================== **/
export const toggleUserStatus = (userId, isActive) => {
  return api.post('/enable_disable_user', { user_id: userId, is_active: isActive });
};

export const rechargeUser = (userId, amount, expiryDate) => {
  return api.post('/recharge_user', {
    user_id: userId,
    amount,
    expiry_date: expiryDate,
  });
};

/** ===================== Add User ===================== **/
export const createUser = (payload) => {
  return api.post('/add_user', payload);
};

export const editUser = (payload) => {
  return api.post('/edit_user', payload);
};

export const getUserDetail = (userId) => {
  return api.post('/user_detail', { user_id: userId });
};

/** ===================== Token Update ===================== **/
export const updateSandboxToken = (userId, newToken) => {
  return api.post('/update_sandbox_token', { user_id: userId, token: newToken });
};

export const updateProductionToken = (userId, newToken) => {
  return api.post('/update_production_token', { user_id: userId, token: newToken });
};

/** ===================== Production Mode Toggle ===================== **/
export const setProductionMode = (userId, is_prod_token, password) => {
  return api.post('/set_production_mode', { user_id: userId, is_prod_token, password });
};

export const impersonateClient = (userId) => {
  return api.post(`/admin_impersonate/${userId}`);
};

export const toggleApproval = (userId, isApproval) => {
  return api.post('/enable_disable_approval', { user_id: userId, is_approval: isApproval });
};

export const setInvoiceBackup = (userId, months) => {
  return api.post('/set_invoice_backup', {
    user_id: userId,
    backup_months: months,
    invoice_history_months: months,
    is_invoice_history: months > 1 ? 1 : 0,
  });
};

const getFbrAuthorizationHeader = ({ token_dev, token_prod, is_prod_token } = {}) => ({
  Authorization: is_prod_token && token_prod ? `Bearer ${token_prod}` : `Bearer ${token_dev || token_prod || ''}`,
});

const fetchFbrReferenceApi = async (url, auth) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...getFbrAuthorizationHeader(auth),
    },
  });

  if (!response.ok) {
    throw new Error(`FBR reference API error: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data : data?.response || data?.data || [];
};

const formatFbrLookupDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  const day = String(parsed.getDate()).padStart(2, '0');
  const month = parsed.toLocaleString('en-US', { month: 'short' });
  const year = parsed.getFullYear();
  return `${day}-${month}-${year}`;
};

export const getFbrHsCodes = (auth) =>
  fetchFbrReferenceApi('https://gw.fbr.gov.pk/pdi/v1/itemdesccode', auth);

export const getFbrProvinces = (auth) =>
  fetchFbrReferenceApi('https://gw.fbr.gov.pk/pdi/v1/provinces', auth);

export const getFbrUoms = (auth) =>
  fetchFbrReferenceApi('https://gw.fbr.gov.pk/pdi/v1/uom', auth);

export const getFbrTransactionTypes = (auth) =>
  fetchFbrReferenceApi('https://gw.fbr.gov.pk/pdi/v1/transtypecode', auth);

export const getFbrRates = ({ date, transTypeId, originationSupplier }, auth) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v2/SaleTypeToRate?date=${encodeURIComponent(formatFbrLookupDate(date))}&transTypeId=${encodeURIComponent(transTypeId)}&originationSupplier=${encodeURIComponent(originationSupplier)}`,
    auth
  );

export const getFbrSroSchedules = ({ rateId, date, originationSupplier }, auth) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v1/SroSchedule?rate_id=${encodeURIComponent(rateId)}&date=${encodeURIComponent(formatFbrLookupDate(date))}&origination_supplier_csv=${encodeURIComponent(originationSupplier)}`,
    auth
  );

export const getFbrSroItems = ({ date, sroId }, auth) =>
  fetchFbrReferenceApi(
    `https://gw.fbr.gov.pk/pdi/v2/SROItem?date=${encodeURIComponent(date)}&sro_id=${encodeURIComponent(sroId)}`,
    auth
  );

/** ===================== Roles ===================== **/
export const getRoles = () => {
  return api.get('/roles');
};

export const addRole = ({ role_name, permissions }) => {
  // If your backend expects a JSON STRING for permissions, use:
  // return api.post('/add_role', { role_name, permissions: JSON.stringify(permissions) });

  // Otherwise, send as an array:
  return api.post('/add_role', { role_name, permissions });
};

export const editRole = ({ role_name,role_id, permissions }) => {
  // If your backend expects a JSON STRING for permissions, use:
  // return api.post('/add_role', { role_name, permissions: JSON.stringify(permissions) });

  // Otherwise, send as an array:
  return api.post('/edit_role', { role_name,role_id, permissions });
};

export const deleteRole = (roleId) => {
  return api.post('/delete_role', { role_id: roleId });
};

/** ===================== Admin Users ===================== **/
export const getAdminUsers = () => {
  return api.get('/admin_users');
};

export const addAdminUser = (payload) => {
  return api.post('/add_admin_user', payload);
};

export const editAdminUser = (payload) => {
  return api.post('/edit_admin_user', payload);
};

export const deleteAdminUser = (adminId) => {
  return api.post('/delete_admin', { id: adminId });
};
